import * as React from 'react';
import { Check, ChevronDown, ChevronRight, Circle, TriangleAlert } from 'lucide-react';
import type { CaseProgressView } from '../lib/case-progress.js';

/**
 * Krok UXV4a — SDILENE KOMPONENTY UX v4. JEDNA IMPLEMENTACE, VSECHNA MISTA.
 *
 * Predloha: `docs/design/ux-v4/DESIGN-UX-V4.md` § Komponenty (schvaleno
 * R.B. 4. 10. 2026 06:11). Sazba je v `styles/ux-v4.css`, hodnoty v
 * `styles/tokens-admin-v2.css` — tady je jen DOM a chovani.
 *
 * Proc jedna sada a ne „kazda obrazovka si nakresli svoje": UX v3 skoncil
 * tim, ze tentyz prvek (nahled nemovitosti, prouzek fazi) zil ve dvou
 * kopiich, ktere se rozesly (nalezy UXR1 a TAB1). Dalsi kroky (detail,
 * tabule, smlouvy, inzerat, nabor) stavi na TECHTO komponentach; kdyz
 * nejaka obrazovka potrebuje neco navic, pridava se to SEM, ne vedle.
 *
 * Co tu zamerne NENI:
 *  - navigacni a spodni lista — to je shell aplikace (`admin-shell.tsx`),
 *    jediny na cely admin; UX v4 mu meni jen tokeny a obsah menu,
 *  - obrazek nemovitosti — ma vlastni soubor (`PropertyImage.tsx`),
 *    protoze nese kaskadu zdroju, ne jen vzhled.
 */

/* -------------------------------------------------- hlavicka s nadpisem */

export function V4Header({
  date,
  title,
  lede,
  note,
  titleTestId,
}: {
  readonly date?: string | undefined;
  readonly title: string;
  readonly lede?: React.ReactNode;
  /** Druhy radek pod pozdravem — veta stavu („smlouva k podpisu · …"). */
  readonly note?: React.ReactNode;
  readonly titleTestId?: string | undefined;
}): React.JSX.Element {
  return (
    <div className="rh-v4-head">
      {date !== undefined ? <div className="rh-v4-head__date">{date}</div> : null}
      <h1 className="rh-v4-head__title" {...(titleTestId !== undefined ? { 'data-testid': titleTestId } : {})}>
        {title}
      </h1>
      {lede !== undefined ? <div className="rh-v4-head__lede">{lede}</div> : null}
      {note !== undefined ? <div className="rh-v4-head__lede">{note}</div> : null}
    </div>
  );
}

/* ----------------------------------------------- seskupeny seznam a radek */

export function V4Group({
  children,
  testId,
  label,
}: {
  readonly children: React.ReactNode;
  readonly testId?: string | undefined;
  /** Pristupny nazev skupiny, kdyz skupina stoji sama za sebe. */
  readonly label?: string | undefined;
}): React.JSX.Element {
  return (
    <section
      className="rh-v4-group"
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
      {...(label !== undefined ? { 'aria-label': label } : {})}
    >
      {children}
    </section>
  );
}

export interface V4RowProps {
  /** Levy sloupec s pevnou sirkou — cas v programu dne (predloha „11:00"). */
  readonly lead?: React.ReactNode;
  /** Nahled nebo ikona pred textem. */
  readonly icon?: React.ReactNode;
  readonly title: React.ReactNode;
  readonly sub?: React.ReactNode;
  /** Hodnota vpravo (predloha: cislo nebo stav). */
  readonly value?: React.ReactNode;
  /** Akce vpravo misto chevronu (predloha: tlacitko „Připomenout"). */
  readonly right?: React.ReactNode;
  readonly chevron?: boolean;
  readonly testId?: string | undefined;
  readonly onClick?: (() => void) | undefined;
  /**
   * Jak se radek vykresli. Odkaz routerem (`<Link>`) zna jen volajici — shell
   * ani tahle komponenta router neimportuji (stejny vzor jako
   * `renderNavItem` v `admin-shell.tsx`). Bez `render` je to `<button>`
   * (kdyz je `onClick`) nebo staticky radek.
   */
  readonly render?: ((className: string, content: React.ReactNode) => React.ReactNode) | undefined;
}

export function V4Row({
  lead,
  icon,
  title,
  sub,
  value,
  right,
  chevron = false,
  testId,
  onClick,
  render,
}: V4RowProps): React.JSX.Element {
  const className = `rh-v4-row${sub !== undefined ? ' rh-v4-row--sub' : ''}`;
  const content = (
    <>
      {lead !== undefined ? <span className="rh-v4-row__lead">{lead}</span> : null}
      {icon !== undefined ? icon : null}
      <span className="rh-v4-row__text">
        <span className="rh-v4-row__title">{title}</span>
        {sub !== undefined ? <span className="rh-v4-row__sub">{sub}</span> : null}
      </span>
      {value !== undefined ? <span className="rh-v4-row__value">{value}</span> : null}
      {right !== undefined ? right : null}
      {chevron ? (
        <span className="rh-v4-row__chev" aria-hidden="true">
          <ChevronRight size={16} strokeWidth={3} />
        </span>
      ) : null}
    </>
  );
  if (render !== undefined) return <>{render(className, content)}</>;
  if (onClick !== undefined) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
      >
        {content}
      </button>
    );
  }
  return (
    <div
      className={`${className} rh-v4-row--static`}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {content}
    </div>
  );
}

/* ------------------------------------------- sekce s rozbalenim (iOS) */

export function V4Section({
  icon,
  label,
  sub,
  extra,
  count,
  open,
  onToggle,
  testId,
  bare = false,
  children,
}: {
  readonly icon?: React.ReactNode;
  readonly label: React.ReactNode;
  /**
   * Druhy radek hlavicky — souhrn sekce (predloha `Zakazka.dc.html`:
   * „2 z 6 v pořádku · 1 problém"). Krok UXV4a2.
   */
  readonly sub?: React.ReactNode;
  /** Treti radek — vlastni prouzek sekce (predloha: 6px progress). */
  readonly extra?: React.ReactNode;
  /** Odznak s poctem vpravo od popisku (predloha „Čeká na tebe · 3"). */
  readonly count?: number | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly testId?: string | undefined;
  /**
   * `true` = sekce NENI vlastni skupina, ale radek uvnitr spolecne skupiny
   * (predloha: Nemovitost · Lidé · Dokumenty · Peníze · Historie stoji v
   * JEDNE bile skupine pod sebou, oddelene linkou).
   */
  readonly bare?: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const head = (
    <>
      <button
        type="button"
        className="rh-v4-sect__head"
        aria-expanded={open}
        onClick={onToggle}
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
      >
        {icon !== undefined ? icon : null}
        <span className="rh-v4-sect__text">
          <span className="rh-v4-sect__label">{label}</span>
          {sub !== undefined ? <span className="rh-v4-sect__sub">{sub}</span> : null}
          {extra !== undefined ? extra : null}
        </span>
        {count !== undefined ? <V4Pill>{count}</V4Pill> : null}
        <span className="rh-v4-sect__chev" aria-hidden="true">
          {open ? <ChevronDown size={14} strokeWidth={3} /> : <ChevronRight size={14} strokeWidth={3} />}
        </span>
      </button>
      {open ? children : null}
    </>
  );
  if (bare) return <div className="rh-v4-sect rh-v4-sect--bare">{head}</div>;
  return <section className="rh-v4-group rh-v4-sect">{head}</section>;
}

/**
 * Krok UXV4a2 — rozbaleni „Proč a co s tím" u problemu (predloha
 * `Zakazka.dc.html`): textove tlacitko, po rozbaleni vysvetleni, co udelat
 * a zdroj. Je to komponenta, a ne `<details>`, protoze predloha meni popisek
 * na „Skrýt" a sada to meri pres `aria-expanded`.
 */
export function V4Why({
  label,
  hideLabel,
  open,
  onToggle,
  testId,
  children,
}: {
  readonly label: string;
  readonly hideLabel: string;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly testId?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <>
      <button
        type="button"
        className="rh-v4-btn rh-v4-btn--plain rh-v4-why__toggle"
        aria-expanded={open}
        onClick={onToggle}
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
      >
        {open ? hideLabel : label}
      </button>
      {open ? (
        <div
          className="rh-v4-why"
          {...(testId !== undefined ? { 'data-testid': `${testId}-body` } : {})}
        >
          {children}
        </div>
      ) : null}
    </>
  );
}

/**
 * Krok UXV4a2 — navigacni lista obrazovky v4 (predloha `Zakazka.dc.html`:
 * „‹ Dnešek" vlevo, kulate „⋯" vpravo). Shell admina ma horni listu jen na
 * mobilu, takze na desktopu by jinak z detailu nevedla cesta zpet jinak nez
 * drobeckem, ktery predloha v4 nema.
 */
export function V4NavBar({
  back,
  actions,
  testId,
}: {
  readonly back: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <div className="rh-v4-nav" {...(testId !== undefined ? { 'data-testid': testId } : {})}>
      <span className="rh-v4-nav__back">{back}</span>
      {actions !== undefined ? <span className="rh-v4-nav__acts">{actions}</span> : null}
    </div>
  );
}

/* ------------------------------------------------- karta „Další krok" */

export function V4NextStep({
  kicker,
  icon,
  title,
  note,
  photo,
  actions,
  testId,
}: {
  readonly kicker: string;
  readonly icon: React.ReactNode;
  readonly title: React.ReactNode;
  readonly note?: React.ReactNode;
  /** Foto karta nad textem (predloha `Main.dc.html`); bez ni je karta plna. */
  readonly photo?: React.ReactNode;
  readonly actions: React.ReactNode;
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <section
      className={`rh-v4-next${photo === undefined ? ' rh-v4-next--plain' : ''}`}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {photo}
      <div className="rh-v4-next__body">
        <div className="rh-v4-next__kicker">
          <span className="rh-v4-ico" aria-hidden="true">
            {icon}
          </span>
          {kicker}
        </div>
        <div className="rh-v4-next__texts">
          <div className="rh-v4-next__title">{title}</div>
          {note !== undefined ? <div className="rh-v4-next__note">{note}</div> : null}
        </div>
        <div className="rh-v4-next__acts">{actions}</div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------- foto karta */

export function V4PhotoCard({
  image,
  chip,
  button,
  height,
  variant = 'card',
}: {
  /** Obrazek nemovitosti — vzdy z `PropertyImage` (jedna kaskada zdroju). */
  readonly image: React.ReactNode;
  readonly chip?: React.ReactNode;
  /** Kulate tlacitko ve vyrezu; vykresleni (odkaz/tlacitko) dodava volajici. */
  readonly button?: React.ReactNode;
  readonly height: number;
  readonly variant?: 'card' | 'hero';
}): React.JSX.Element {
  return (
    <div className={`rh-v4-photo${variant === 'hero' ? ' rh-v4-photo--hero' : ''}`} style={{ height }}>
      <div className="rh-v4-photo__frame">{image}</div>
      {chip !== undefined ? <span className="rh-v4-photo__chip">{chip}</span> : null}
      {button !== undefined ? <span className="rh-v4-photo__notch">{button}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------- progress bar */

/**
 * Holy prouzek (bez cisla a vety). Krok UXV4a2: `xs` = 6 px v hlavicce sekce
 * (predloha `Zakazka.dc.html` — „Prověření" ma vlastni prouzek pod souhrnem).
 * Je to TATAZ sazba, jakou pouziva `V4Progress`, aby prouzek nevznikl podruhe
 * jinym zpusobem.
 */
export function V4Bar({
  percent,
  size = 'sm',
  tone = 'accent',
  label,
  testId,
}: {
  readonly percent: number;
  readonly size?: 'xs' | 'sm' | 'lg';
  readonly tone?: 'accent' | 'done';
  /** Pristupny popis pro ctecku, kdyz prouzek stoji bez cisla vedle sebe. */
  readonly label?: string | undefined;
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <span
      className={`rh-v4-prog rh-v4-prog--${size}${tone === 'done' ? ' rh-v4-prog--done' : ''}`}
      data-percent={percent}
      {...(label !== undefined ? { role: 'img', 'aria-label': label } : {})}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      <span className="rh-v4-prog__track">
        <span className="rh-v4-prog__fill" style={{ width: `${String(percent)}%` }} />
      </span>
    </span>
  );
}

/**
 * Prouzek postupu zakazky. Cisla, veta „co chybí" i kroky pochazeji z JEDNE
 * funkce (`lib/case-progress.ts`) — tady se uz nic nepocita, jen kresli.
 *
 * `size`: `sm` = 8 px (seznam, predloha § Pravidla 5), `lg` = 12 px (detail).
 */
export function V4Progress({
  view,
  size = 'sm',
  steps = false,
  testId,
  onStepSelect,
}: {
  readonly view: CaseProgressView;
  readonly size?: 'sm' | 'lg';
  readonly steps?: boolean;
  readonly testId?: string | undefined;
  /**
   * Krok QAFIX11 bod 1 (nalez V5): otevre sekci detailu, kterou krok
   * zastupuje (`CaseProgressStep.section`). Bez teto funkce — v seznamu
   * a na kanbanu, kde zadna sekce po ruce neni — zustava krok obycejnym
   * textem, takze nikde neslibuje klik, ktery neumi.
   */
  readonly onStepSelect?: ((section: string) => void) | undefined;
}): React.JSX.Element {
  const done = view.percent >= 80;
  return (
    <div
      className={`rh-v4-prog${size === 'lg' ? ' rh-v4-prog--lg' : ''}${done ? ' rh-v4-prog--done' : ''}`}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
      data-percent={view.percent}
    >
      <div className="rh-v4-prog__top">
        {/* Predloha: v karte detailu „Hotovo 40 %", v radku seznamu jen „40 %". */}
        <span className="rh-v4-prog__pct">{size === 'lg' ? view.percentDoneLabel : view.percentLabel}</span>
        <span className="rh-v4-prog__missing">{view.missing}</span>
      </div>
      <div className="rh-v4-prog__track">
        <span className="rh-v4-prog__fill" style={{ width: `${String(view.percent)}%` }} />
      </div>
      {steps ? (
        <div className="rh-v4-prog__steps">
          {view.steps.map((step) => {
            /*
              Krok PIL3 bod 8 (nalez F07): „✓" tvrdi, ze se oblast v REOSu
              odbavila. U oblasti, kterou uzivatelka nemuze ani otevrit
              (`unavailable` — zalozka je „Připravuje se"), se proto nekresli
              zadna znacka; krok zustava v prouzku, jen bez odskrtnuti.
            */
            const mark = step.state === 'done' ? '✓ ' : step.state === 'now' ? '● ' : '';
            const className = `rh-v4-prog__step rh-v4-prog__step--${step.state}`;
            const section = step.section;
            /*
              Krok QAFIX11 bod 1 (nalez V5): krok se SEKCI je skutecna akce
              (`<button>` — mysi, klavesnici i ctecce), krok bez sekce zustava
              textem. Trida `--link` je jen na te podobe, ktera klik opravdu
              umi, takze vzhled odkazu a navigace nemohou jit od sebe.
            */
            if (section === null || onStepSelect === undefined) {
              return (
                <span key={step.id} className={className} data-state={step.state}>
                  {mark}
                  {step.shortLabel}
                </span>
              );
            }
            return (
              <button
                key={step.id}
                type="button"
                className={`${className} rh-v4-prog__step--link`}
                data-state={step.state}
                data-testid={`case-progress-step-${step.id}`}
                onClick={() => { onStepSelect(section); }}
              >
                {mark}
                {step.shortLabel}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------- krokovnik */

export interface V4Step {
  /** Stabilni klic — `data-testid` a trida `--<id>`. */
  readonly id: string;
  readonly label: string;
  readonly state: 'done' | 'now' | 'wait';
  /**
   * Co stoji v kolecku misto ✓ / ○ — predloha formulare ma cisla kroku
   * („1 Nemovitost"), predloha detailu znacky stavu.
   */
  readonly badge?: React.ReactNode | undefined;
}

/**
 * Krok GRAF5 — KROKOVNIK (predlohy `maklerov-grafika-detail` a
 * `…-formular`). JEDNA komponenta pro obe obrazovky: detail jim kresli pet
 * fazi zakazky, formular ctyri kroky pruvodce. Dve kopie by se rozesly uz
 * pri prvni zmene vysky nebo odstinu „aktivniho" kroku — a to je prave to
 * misto, kde predloha urcuje, jak obrazovka vypada.
 *
 * Krokovnik je ZNACKA STAVU, ne navigace: nikam neklika. Kroky, na ktere se
 * da prejit, maji svoje vlastni tlacitko jinde (pruh postupu, „Pokračovat").
 */
export function V4Steps({
  steps,
  testId,
}: {
  readonly steps: readonly V4Step[];
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <ol className="rh-v4-steps" {...(testId !== undefined ? { 'data-testid': testId } : {})}>
      {steps.map((step) => (
        <li
          key={step.id}
          className={`rh-v4-steps__item rh-v4-steps__item--${step.state}`}
          data-state={step.state}
          {...(testId !== undefined ? { 'data-testid': `${testId}-${step.id}` } : {})}
          /* Stav se nese i do pristupneho stromu — barva sama ho nerekne. */
          aria-current={step.state === 'now' ? 'step' : undefined}
        >
          <span className="rh-v4-steps__mark" aria-hidden="true">
            {step.badge ?? (step.state === 'done' ? <Check size={16} strokeWidth={3} /> : <Circle size={16} strokeWidth={2} />)}
          </span>
          <span className="rh-v4-steps__label">{step.label}</span>
        </li>
      ))}
    </ol>
  );
}

/* -------------------------------------------- kapsle a stavove ikony */

export function V4Pill({
  children,
  tone = 'neutral',
  testId,
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'neutral' | 'accent' | 'warn';
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <span
      className={`rh-v4-pill${tone === 'neutral' ? '' : ` rh-v4-pill--${tone}`}`}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {children}
    </span>
  );
}

/**
 * Stavova ikona predlohy: plne kolecko s ✓ (hotovo), trojuhelnik (ceka na
 * uzivatele), prazdne kolecko (ceka se na nekoho jineho).
 */
export function V4State({ state }: { readonly state: 'done' | 'wait' | 'later' }): React.JSX.Element {
  return (
    <span className={`rh-v4-state rh-v4-state--${state}`} aria-hidden="true">
      {state === 'done' ? (
        <Check size={18} strokeWidth={3} />
      ) : state === 'wait' ? (
        <TriangleAlert size={18} strokeWidth={2} />
      ) : (
        <Circle size={18} strokeWidth={2} />
      )}
    </span>
  );
}

/* ------------------------------------- prepinac a formularovy radek */

export function V4Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  testId,
}: {
  readonly value: T;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly onChange: (value: T) => void;
  readonly label: string;
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <div
      className="rh-v4-seg"
      role="group"
      aria-label={label}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? 'is-on' : ''}
          aria-pressed={value === option.value}
          data-testid={`${testId ?? 'seg'}-${option.value}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function V4FormRow({
  label,
  hint,
  stack = false,
  children,
}: {
  readonly label: React.ReactNode;
  readonly hint?: React.ReactNode;
  /**
   * Krok UXF4 bod 5 (nalez N8 reauditu GPT 10. 10. 2026) — NAZEV NAD POLEM,
   * ne vedle nej. Radek s nazvem VLEVO a prepinacem vpravo funguje u
   * zaskrtavatka nebo kratke hodnoty; u pasu cipu („Světlý · Tmavý · Podle
   * systému") se na 390 px nazev zmackne a vypada jako oriznuty text.
   * Takove radky dostanou `stack`: popisek na vlastnim radku, cipy pod nim
   * pres celou sirku karty (a v druhe rade, kdyz se nevejdou).
   */
  readonly stack?: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  /*
   * Krok UXD4 (nalez V1 UX auditu 21. 10., axe `label`, impact CRITICAL) —
   * POPISEK PATRI K POLI, NE JEN NAD NEJ. Do UXD4 byl nazev radku `<span>`,
   * takze cely formular zmeny hesla vypadal pro ctecku obrazovky jako tri
   * nepopsana policka („Současné", „Nové", „Potvrzení" videl jen ten, kdo
   * obrazovku VIDI). Pravidlo je tady, v radku, ne u jednotlivych polí —
   * jinak by kazda obrazovka resila popisky znovu a jedna by to zapomnela.
   *
   * Sparuje se JEN jedno jedine policko bez vlastniho popisku: radek, ktery
   * nese pas cipu nebo prepinac s vlastnim `aria-label` (`V4Switch`,
   * `V4Seg`), zustava nedotceny — obalit `<label>`em skupinu tlacitek by
   * znamenalo, ze klik kdekoli v radku zmackne to prvni z nich.
   */
  const generatedId = React.useId();
  const only = React.isValidElement(children) ? (children as React.ReactElement<Record<string, unknown>>) : null;
  const labelable =
    only !== null && (only.type === 'input' || only.type === 'select' || only.type === 'textarea')
      ? only
      : null;
  const alreadyLabelled =
    labelable !== null &&
    (typeof labelable.props['aria-label'] === 'string' || typeof labelable.props['aria-labelledby'] === 'string');
  const fieldId =
    labelable === null || alreadyLabelled
      ? null
      : typeof labelable.props['id'] === 'string'
        ? labelable.props['id']
        : generatedId;
  const field =
    fieldId === null || labelable === null
      ? children
      : React.cloneElement(labelable, { id: fieldId });

  return (
    <div className={`rh-v4-form-row${stack ? ' rh-v4-form-row--stack' : ''}`}>
      {fieldId === null ? (
        <span className="rh-v4-form-row__label">{label}</span>
      ) : (
        <label className="rh-v4-form-row__label" htmlFor={fieldId}>
          {label}
        </label>
      )}
      <span className="rh-v4-form-row__field">{field}</span>
      {hint !== undefined ? <span className="rh-v4-form-row__hint">{hint}</span> : null}
    </div>
  );
}

/**
 * Prepinac iOS (komponenta predlohy „Přepínač iOS (zelený)", krok UXV4b —
 * `Zverejnit.dc.html`, „Kde inzerovat"). Vypnuty a `disabled` = „připravujeme".
 */
export function V4Switch({
  checked,
  onChange,
  label,
  disabled = false,
  testId,
}: {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  /** Pristupny nazev (nazev portalu) — prepinac nema viditelny popisek. */
  readonly label: string;
  readonly disabled?: boolean;
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`rh-v4-switch${checked ? ' is-on' : ''}`}
      onClick={() => onChange(!checked)}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      <span className="rh-v4-switch__knob" aria-hidden="true" />
    </button>
  );
}
